import Box from "@mui/material/Box";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import { SelectChangeEvent } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as StockLevel } from "assets/SideBarIcons/stock-level.svg";
import BackendPaginationTable from "common/Table/TableWithPagination";
import Loader from "layout/Loader";
import { useGetItemsService } from "subModule/src/services/items/useItems";
import { useGetStoreDropdownService } from "subModule/src/services/store/useStore";
import StockLevelSearchHeader from "components/Inventory/StockLevelSearchHeader";
interface SearchFormData {
  [key: string]: string | any;
}
export default function StockLevelListing() {
  const navigate = useNavigate();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [filter, setFilter] = useState<string[]>([
    "name",
    "itemCode",
    "storeId",
  ]);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const {
    data: storesData,
    isFetching: storeFetching,
    refetch: storesRefetch,
  } = useGetStoreDropdownService({
    pageNumber: dropdownPage,
    size: 50,
    title: dropdownSearchTerm,
  });
  const [search, setSearch] = useState<any>({ storeId: null });
  const [searchFormData, setSearchFormData] = useState<SearchFormData>({
    name: "",
    itemCode: "",
    storeId: null,
    storeTitle: "",
  });
  const {
    data: tableData,
    isFetching,
    refetch,
  } = useGetItemsService(
    search.storeId
      ? {
          pageNumber: pageNo,
          size: pageSize,
          category: 2,
          active: true,
          ...search,
        }
      : {
          enabled: false,
          size: pageSize,
          category: 2,
          active: true,
          ...Object.fromEntries(
            Object.entries(search).filter(([k]) => k !== "storeId"),
          ),
        },
  );
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const firstRender = useRef(true);
  const storeOptions = (storesData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const fetchData = useCallback(() => {
    refetch().then((res: any) => {
      if (!res?.data?.pages[0]?.success)
        enqueueSnackbar(
          <AlertMessages
            text={res?.data?.pages[0]?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
    });
  }, []);
  useEffect(() => {
    storesRefetch().then((res: any) => {
      if (res?.data?.pages[0]?.success) {
        if (res?.data?.pages[0]?.data?.content?.length > 0) {
          const firstStoreId = res?.data?.pages[0]?.data?.content[0].id;
          setSearchFormData((prev) => ({
            ...prev,
            storeId: firstStoreId,
            storeTitle: res?.data?.pages[0]?.data?.content[0].title,
          }));
          setSearch({ storeId: firstStoreId });
        } else setSearch({});
      } else
        enqueueSnackbar(
          <AlertMessages
            text={res?.data?.pages[0]?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
    });
  }, []);
  useEffect(() => {
    if (storesData === undefined) return;
    fetchData();
  }, [pageNo, pageSize, search]);
  useEffect(() => {
    if (!firstRender.current) handleSubmitSearch();
  }, [searchFormData.storeId]);
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setDropdownPage(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setDropdownPage(dropdownPage + 1);
      setSearchLoading(typeName);
    }
    firstRender.current = false;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "stores")
        storesRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const clearSearch = () => {
    setFilter(["name", "itemCode", "storeId"]);
    setSearchFormData({
      name: "",
      itemCode: "",
      storeId: search.storeId,
      storeTitle: searchFormData.storeTitle,
    });
    if (Object.keys(search).length) {
      setSearch({ storeId: search.storeId });
    }
  };
  const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
    const {
      target: { value },
    } = event;
    setFilter(typeof value === "string" ? value.split(",") : value);
    setSearchFormData((prevFormData) => {
      Object.fromEntries(
        Object.keys(prevFormData).map((key) => [
          key,
          value.includes(key) ? prevFormData[key] : "",
        ]),
      );
    });
    if (!value.length && Object.keys(search).length) {
      clearSearch();
    }
  };
  const handleChangeSearch = (field: string, value: any) => {
    firstRender.current = false;
    const val = field === "storeId" ? parseInt(value.value) : value;
    if (field === "storeId" && searchFormData.storeId === val) return;
    setSearchFormData((prevData) => ({
      ...prevData,
      [field]: val,
      ...(field === "storeId" && value?.label
        ? { storeTitle: value.label }
        : {}),
    }));
  };
  const handleSubmitSearch = () => {
    const filteredSearch: Record<string, string> = {};
    Object.entries(searchFormData).forEach(([key, value]) => {
      if (value) {
        if (key === "name") {
          filteredSearch["title"] = value.trim();
        } else {
          filteredSearch[key] =
            typeof value === "string" ? value?.trim() : value;
        }
      }
    });
    if (
      Object.keys(filteredSearch).length > 0 &&
      Object.values(filteredSearch).some((value) => value)
    ) {
      setSearch(filteredSearch);
      setPageNo(1);
      setPageSize(pageSize);
    }
  };
  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={false}
        showSaveBtn={false}
        onClick={() => navigate("/part/create")}
        heading={t("stockLevel")}
        icon={<StockLevel height={28} width={28} />}
        btnText={"create"}
        btnType={"create"}
        showCreateBtn={false}
      />
      {isFetching || storeFetching ? (
        <Loader />
      ) : (
        <BackendPaginationTable
          columns={[
            { id: "title", label: "partName", numeric: false, width: "24%" },
            { id: "itemCode", label: "itemCode", numeric: false, width: "16%" },
            {
              id: "price",
              label: "ROLE_ITEM_SALE_PRICE",
              numeric: false,
              width: "14%",
            },
            { id: "itemCost", label: "cost", numeric: false, width: "14%" },
            { id: "storeTitle", label: "store", numeric: false, width: "18%" },
            { id: "stockLevel", label: "Stock", numeric: false, width: "14%" },
          ]}
          data={tableData?.pages[0]?.data?.content || []}
          pageNo={pageNo}
          pageSize={pageSize}
          tableData={tableData?.pages[0]?.data || {}}
          showCheckbox={false}
          showActions={true}
          enableSorting={true}
          showHeader={true}
          showSearch={true}
          showFilter={true}
          showCreateButton={true}
          actions={[]}
          handleChangePage={handleChangePage}
          handleChangeRows={handleChangeRows}
          rowClick={true}
          loadingSearch={false}
          headerComponent={
            <>
              <StockLevelSearchHeader
                searchFormData={searchFormData}
                handleChangeFilter={handleChangeFilter}
                handleChangeSearch={handleChangeSearch}
                handleSubmitSearch={handleSubmitSearch}
                clearSearch={clearSearch}
                stores={storeOptions || []}
                storesResponse={storesData || {}}
                handleDropdownActions={handleDropdownActions}
                {...{
                  filter,
                  search,
                  dropdownPage,
                  searchLoading,
                  dropdownSearchTerm,
                  setDropdownSearchTerm,
                  setDropdownPage,
                }}
              />
            </>
          }
        />
      )}
    </Box>
  );
}
